import React from "react";
import {
  Card,
  Table,
  Alert,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Divider,
  Select,
} from "antd";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

const { Text } = Typography;
const { Option } = Select;

const RiskAnalysisTab = ({
  riskAnalysisData,
  profitLossChartData,
  selectedStrike,
  setSelectedStrike,
  strikes,
  expectedMove,
  currentStrikeData,
}) => {
  const marketMid =
    currentStrikeData?.bid != null && currentStrikeData?.ask != null
      ? (currentStrikeData.bid + currentStrikeData.ask) / 2
      : null;
  const riskColumns = [
    {
      title: "Move %",
      dataIndex: "movePercent",
      key: "movePercent",
      render: (text, record) => (
        <Tag color={record.isProfit ? "green" : "red"}>{text}</Tag>
      ),
      width: 100,
    },
    {
      title: "Stock Price",
      dataIndex: "futureStockPrice",
      key: "futureStockPrice",
      render: (val) => `$${val.toFixed(2)}`,
      width: 130,
    },
    {
      title: "Return %",
      dataIndex: "returnPct",
      key: "returnPct",
      render: (val) => (
        <Tag color={val > 0 ? "green" : "red"}>
          {val > 0 ? "+" : ""}
          {val.toFixed(2)}%
        </Tag>
      ),
      sorter: (a, b) => a.returnPct - b.returnPct,
      width: 120,
    },
    {
      title: "Profit/Loss",
      dataIndex: "priceChange",
      key: "priceChange",
      render: (val) => (
        <Text
          strong
          style={{
            color: val > 0 ? "#52c41a" : "#ff4d4f",
            fontSize: 14,
          }}
        >
          {val > 0 ? "+" : ""}${val.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.priceChange - b.priceChange,
      width: 130,
    },
    {
      title: "Option Exit Price",
      dataIndex: "exitPrice",
      key: "exitPrice",
      render: (val) => `$${val.toFixed(2)}`,
      width: 150,
    },
    {
      title: "Loss Amount",
      dataIndex: "lossAmount",
      key: "lossAmount",
      render: (val) =>
        val > 0 ? (
          <Text style={{ color: "#ff4d4f" }}>-${val.toFixed(2)}</Text>
        ) : (
          <Text style={{ color: "#52c41a" }}>No Loss</Text>
        ),
      width: 130,
    },
    {
      title: "Risk Level",
      dataIndex: "riskLevel",
      key: "riskLevel",
      render: (level) => {
        const colors = {
          critical: "red",
          high: "orange",
          medium: "gold",
          safe: "green",
        };
        const labels = {
          critical: "🔴 Critical",
          high: "🟠 High",
          medium: "🟡 Medium",
          safe: "🟢 Safe",
        };
        return <Tag color={colors[level]}>{labels[level]}</Tag>;
      },
      filters: [
        { text: "Critical", value: "critical" },
        { text: "High", value: "high" },
        { text: "Medium", value: "medium" },
        { text: "Safe", value: "safe" },
      ],
      onFilter: (value, record) => record.riskLevel === value,
      width: 130,
    },
  ];

  return (
    <>
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Strike Price:</Text>
            <Select
              style={{ width: "100%", marginTop: 8 }}
              value={selectedStrike}
              onChange={setSelectedStrike}
              placeholder="Select strike to analyze"
            >
              {strikes.map((s) => (
                <Option key={s.strike} value={s.strike}>
                  ${s.strike}
                  {s.iv != null ? ` (IV ${(s.iv * 100).toFixed(1)}%)` : ""}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      {riskAnalysisData.breakEven && (
        <Alert
          message="Break-Even Analysis"
          description={
            <>
              <Row gutter={16} style={{ marginTop: 12 }}>
                <Col span={6}>
                  <Statistic
                    title="Break-Even Stock Price"
                    value={riskAnalysisData.breakEven.stockPrice}
                    precision={2}
                    prefix="$"
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Required Move"
                    value={riskAnalysisData.breakEven.move}
                    precision={2}
                    suffix="%"
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Model Entry Cost"
                    value={riskAnalysisData.entryPrice}
                    precision={2}
                    prefix="$"
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Live Market Price"
                    value={marketMid ?? undefined}
                    precision={2}
                    prefix="$"
                    valueStyle={{ fontSize: 18 }}
                    formatter={marketMid == null ? () => "—" : undefined}
                  />
                  {currentStrikeData?.bid != null &&
                    currentStrikeData?.ask != null && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Bid ${currentStrikeData.bid.toFixed(2)} / Ask $
                        {currentStrikeData.ask.toFixed(2)}
                      </Text>
                    )}
                </Col>
              </Row>
              {marketMid != null && (
                <Text
                  type="secondary"
                  style={{ fontSize: 12, marginTop: 8, display: "block" }}
                >
                  Model is{" "}
                  {(
                    ((riskAnalysisData.entryPrice - marketMid) / marketMid) *
                    100
                  ).toFixed(1)}
                  %{" "}
                  {riskAnalysisData.entryPrice >= marketMid ? "above" : "below"}{" "}
                  the live mid-price.
                </Text>
              )}
            </>
          }
          type="warning"
          style={{ marginBottom: 24 }}
        />
      )}

      <Card
        title={
          <Space>
            <Text strong style={{ color: "#ff4d4f" }}>
              ⚠️ All Profit/Loss Scenarios for Strike ${selectedStrike}
            </Text>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          dataSource={riskAnalysisData.scenarios}
          pagination={false}
          rowKey="movePercent"
          size="small"
          scroll={{ x: 1000 }}
          rowClassName={(record) => {
            if (record.riskLevel === "critical") return "risk-critical";
            if (record.riskLevel === "high") return "risk-high";
            if (record.riskLevel === "medium") return "risk-medium";
            return "";
          }}
          columns={riskColumns}
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="📊 Return % by Stock Movement">
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={profitLossChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="move" />
                <YAxis />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === "Return %") return `${value.toFixed(2)}%`;
                    return value;
                  }}
                />
                <Legend />
                <Bar dataKey="Return %" name="Return %">
                  {profitLossChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry["Return %"] > 0
                          ? entry.move === `${expectedMove}%`
                            ? "#1890ff"
                            : "#52c41a"
                          : "#ff4d4f"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <Divider />
            <Text type="secondary">
              <strong>Note:</strong> Red bars indicate losses. Your target (
              {expectedMove}% move) is highlighted in blue.
            </Text>
          </Card>
        </Col>
      </Row>

      <Card
        title="⏰ Time Decay Analysis - How Your Position Decays Over Time"
        style={{ marginTop: 24 }}
      >
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={riskAnalysisData.timeDecay}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              label={{
                value: "Trading Days Passed",
                position: "insideBottom",
                offset: -5,
              }}
            />
            <YAxis
              yAxisId="left"
              label={{
                value: "Option Price ($)",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              label={{
                value: "Return %",
                angle: 90,
                position: "insideRight",
              }}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === "Return %") return `${value.toFixed(2)}%`;
                return `$${value.toFixed(2)}`;
              }}
            />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="exitPrice"
              stroke="#8884d8"
              strokeWidth={2}
              name="Option Exit Price"
              dot={{ r: 5 }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="entryPrice"
              stroke="#82ca9d"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Entry Price"
              dot={{ r: 3 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="returnPct"
              stroke="#ff7300"
              strokeWidth={2}
              name="Return %"
              dot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <Divider />
        <Alert
          message="Understanding Time Decay"
          description={
            <div>
              <p>
                <strong>Time decay (Theta)</strong> works against you. As days
                pass, your option loses value even if the stock price stays the
                same.
              </p>
              <ul>
                <li>Green dashed line = Your entry price (what you paid)</li>
                <li>
                  Blue line = Option exit price for your {expectedMove}% target
                  at different days
                </li>
                <li>Orange line = Your return % at different time points</li>
              </ul>
              <p>
                <strong>Key Insight:</strong> The longer you wait, the more the
                stock needs to move to maintain the same profit.
              </p>
            </div>
          }
          type="info"
          showIcon
        />
      </Card>
    </>
  );
};

export default RiskAnalysisTab;
